/**
 * @fileoverview Genogram Movement Tool for Arrow Key Shape Movement
 * 
 * This tool provides keyboard-based movement functionality for selected genogram
 * nodes using arrow keys. It integrates with the existing command system to provide
 * undoable movement operations.
 * 
 * Features:
 * - Arrow key movement (10px increments)
 * - Works with single and multiple selections
 * - Undoable operations via MoveNodesCommand
 * - Only active when nodes are selected
 * 
 * @author Claude Code
 * @version 1.0.0
 */

import { BaseTool } from '../../../core/tools/interfaces/ITool';
import { IToolManifest, IToolContext } from '../../../core/tools/interfaces/ITool';
import { ICommand } from '../../../core/commands/ICommand';
import { MoveNodesCommand } from './GenogramTransformCommands';
import { Node } from '../../../core/models/scene';
import i18n from '@/i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';
import { expandNodeIdsWithGroupMembers } from '../../../lib/diagram/grouping';

/**
 * Genogram-specific tool for moving selected nodes using arrow keys.
 * 
 * Provides keyboard-based movement functionality with 10px increments
 * in cardinal directions. Integrates with the command system for
 * undo/redo support and works with both single and multiple selections.
 * 
 * @example
 * ```typescript
 * const movementTool = new GenogramMovementTool();
 * movementTool.setNodesAndUpdater(nodes, updateNode);
 * toolRegistry.register(movementTool);
 * 
 * // Tool will automatically handle arrow key presses when active
 * // and nodes are selected
 * ```
 */
export class GenogramMovementTool extends BaseTool {
  /** Movement distance in pixels */
  private static readonly MOVEMENT_DISTANCE = 10;
  
  /** Reference to current nodes data */
  private nodes: Node[] = [];
  
  /** Function to update node data */
  private updateNode: ((id: string, updates: Partial<Node>) => void) | null = null;
  
  manifest: IToolManifest = {
    id: 'genogram-movement',
    name: i18n.t('genogram:tools.movement', { defaultValue: 'Movement' }) as string,
    icon: '⬅️',
    category: 'transform',
    description: 'Move nodes with arrow keys',
    version: '1.0.0',
    
    // Context requirements
    supportedDiagramTypes: ['genogram'],
    supportedShapeTypes: ['person'],
    requiresSelection: true,
    minSelection: 1,
    
    // UI mounting - this tool doesn't need visible UI
    ui: {
      toolbar: false,
      contextMenu: false,
      sidebar: false,
      propertyPanel: false,
      floatingPanel: false,
      statusBar: false
    },
    
    // Capabilities
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: false,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    },
    
    // Keyboard shortcuts for arrow key movement
    shortcuts: [
      {
        key: 'ArrowUp',
        modifiers: [],
        description: 'Move selection up'
      },
      {
        key: 'ArrowDown',
        modifiers: [],
        description: 'Move selection down'
      },
      {
        key: 'ArrowLeft',
        modifiers: [],
        description: 'Move selection left'
      },
      {
        key: 'ArrowRight',
        modifiers: [],
        description: 'Move selection right'
      }
    ]
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.movement', 'Movement');
  }
  
  /**
   * Set the nodes data and update function.
   * This is called by the parent component to provide data access.
   */
  setNodesAndUpdater(
    nodes: Node[], 
    updateNode: (id: string, updates: Partial<Node>) => void
  ): void {
    this.nodes = nodes;
    this.updateNode = updateNode;
  }
  
  canExecute(context: IToolContext): boolean {
    // Check base requirements
    if (!super.canExecute(context)) {
      return false;
    }
    
    // Ensure we have nodes and updater function
    if (!this.updateNode || this.nodes.length === 0) {
      return false;
    }
    
    // Check that selected items are person nodes
    const selectedNodeIds = Array.from(context.selection);
    const validNodes = selectedNodeIds.filter(id => 
      this.nodes.find(n => n.id === id && n.type === 'person')
    );
    
    return validNodes.length > 0;
  }
  
  /**
   * Execute movement based on direction parameter.
   * 
   * @param params - Movement parameters containing direction
   * @returns Command for undoable movement operation
   */
  execute(params?: { direction: 'up' | 'down' | 'left' | 'right' }): ICommand | null {
    if (!this.context || !params?.direction || !this.updateNode) {
      return null;
    }
    
    const selectedIds = Array.from(this.context.selection);
    if (selectedIds.length === 0) {
      return null;
    }
    
    // Filter to only person nodes that exist
    const expandedIds = expandNodeIdsWithGroupMembers(selectedIds, this.nodes);
    const validNodeIds = Array.from(expandedIds).filter(id => 
      this.nodes.find(n => n.id === id && n.type === 'person')
    );
    
    if (validNodeIds.length === 0) {
      return null;
    }
    
    // Calculate movement delta based on direction
    const delta = this.getMovementDelta(params.direction);
    
    // Get current positions and calculate new positions
    const moves = validNodeIds.map(nodeId => {
      const node = this.nodes.find(n => n.id === nodeId);
      if (!node) {
        return null;
      }
      
      const currentPos = { x: node.x, y: node.y };
      const newPos = {
        x: currentPos.x + delta.x,
        y: currentPos.y + delta.y
      };
      
      return {
        nodeId,
        oldPosition: currentPos,
        newPosition: newPos
      };
    }).filter(Boolean) as Array<{
      nodeId: string;
      oldPosition: { x: number; y: number };
      newPosition: { x: number; y: number };
    }>;
    
    if (moves.length === 0) {
      return null;
    }
    
    // Create movement command
    return new MoveNodesCommand(moves);
  }
  
  /**
   * Move selection up by the standard movement distance.
   */
  moveUp(): ICommand | null {
    return this.execute({ direction: 'up' });
  }
  
  /**
   * Move selection down by the standard movement distance.
   */
  moveDown(): ICommand | null {
    return this.execute({ direction: 'down' });
  }
  
  /**
   * Move selection left by the standard movement distance.
   */
  moveLeft(): ICommand | null {
    return this.execute({ direction: 'left' });
  }
  
  /**
   * Move selection right by the standard movement distance.
   */
  moveRight(): ICommand | null {
    return this.execute({ direction: 'right' });
  }
  
  /**
   * Calculate movement delta based on direction.
   */
  private getMovementDelta(direction: 'up' | 'down' | 'left' | 'right'): { x: number; y: number } {
    const distance = GenogramMovementTool.MOVEMENT_DISTANCE;
    
    switch (direction) {
      case 'up':
        return { x: 0, y: -distance };
      case 'down':
        return { x: 0, y: distance };
      case 'left':
        return { x: -distance, y: 0 };
      case 'right':
        return { x: distance, y: 0 };
      default:
        return { x: 0, y: 0 };
    }
  }
}
